import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Car, 
  Calendar, 
  MapPin, 
  User, 
  Phone, 
  Mail, 
  Clock, 
  CreditCard,
  CheckCircle,
  Loader2,
  ArrowLeft,
  DollarSign,
  Wine,
  Home,
  UserCheck,
  Navigation,
  Timer
} from 'lucide-react';

const LOGO_URL = "/onyx-reserve-logo.png";
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const DriveYourCar = () => {
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [calculatingRoute, setCalculatingRoute] = useState(false);
  const [quote, setQuote] = useState(null);
  const [paymentLink, setPaymentLink] = useState('');
  const [mapsLoaded, setMapsLoaded] = useState(false);
  
  const pickupInputRef = useRef(null);
  const dropoffInputRef = useRef(null);
  const pickupAutocompleteRef = useRef(null);
  const dropoffAutocompleteRef = useRef(null);
  
  const [formData, setFormData] = useState({
    service_option: '',
    pickup_location: '',
    dropoff_location: '',
    date: '',
    time: '',
    estimated_miles: '',
    estimated_hours: '',
    estimated_duration_text: '',
    driver_return_miles: '',
    vehicle_type: 'standard',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    vehicle_color: '',
    name: '',
    email: '',
    phone: '',
    notes: ''
  });

  const serviceOptions = [
    {
      id: 'drive_me_home',
      icon: Wine,
      title: 'Drive Me Home',
      description: 'Had a few drinks? We\'ll drive you AND your car home safely.',
      pricing: 'Starting at $150',
      color: 'purple'
    },
    {
      id: 'take_car_home',
      icon: Home,
      title: 'Take My Car Home',
      description: 'Keep partying! We\'ll take your car home for you.',
      pricing: 'Starting at $150',
      color: 'blue'
    },
    {
      id: 'personal_chauffeur',
      icon: UserCheck,
      title: 'Personal Chauffeur',
      description: 'Use your own car instead of Uber. We\'ll drive you around.',
      pricing: '$65/hour flat rate',
      color: 'green'
    }
  ];

  // Load Google Maps Script
  useEffect(() => {
    if (window.google && window.google.maps) {
      setMapsLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapsLoaded(true);
    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Initialize autocomplete when maps loaded and step is 2
  useEffect(() => {
    if (mapsLoaded && step === 2) {
      initAutocomplete();
    }
  }, [mapsLoaded, step]);

  const initAutocomplete = () => {
    if (!window.google || !window.google.maps || !window.google.maps.places) return;

    // Pickup autocomplete
    if (pickupInputRef.current && !pickupAutocompleteRef.current) {
      pickupAutocompleteRef.current = new window.google.maps.places.Autocomplete(
        pickupInputRef.current,
        { types: ['address'], componentRestrictions: { country: 'us' } }
      );
      pickupAutocompleteRef.current.addListener('place_changed', () => {
        const place = pickupAutocompleteRef.current.getPlace();
        if (place.formatted_address) {
          setFormData(prev => ({ ...prev, pickup_location: place.formatted_address }));
        }
      });
    }

    // Dropoff autocomplete
    if (dropoffInputRef.current && !dropoffAutocompleteRef.current) {
      dropoffAutocompleteRef.current = new window.google.maps.places.Autocomplete(
        dropoffInputRef.current,
        { types: ['address'], componentRestrictions: { country: 'us' } }
      );
      dropoffAutocompleteRef.current.addListener('place_changed', () => {
        const place = dropoffAutocompleteRef.current.getPlace();
        if (place.formatted_address) {
          setFormData(prev => ({ ...prev, dropoff_location: place.formatted_address }));
        }
      });
    }
  };

  // Calculate route when both addresses are set
  const calculateRoute = useCallback(async () => {
    if (!formData.pickup_location || !formData.dropoff_location) return;
    if (!window.google || !window.google.maps) return;

    setCalculatingRoute(true);

    try {
      const service = new window.google.maps.DistanceMatrixService();
      
      service.getDistanceMatrix(
        {
          origins: [formData.pickup_location],
          destinations: [formData.dropoff_location],
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.IMPERIAL,
        },
        (response, status) => {
          setCalculatingRoute(false);
          
          if (status === 'OK' && response.rows[0].elements[0].status === 'OK') {
            const element = response.rows[0].elements[0];
            const distanceText = element.distance.text; // e.g., "15.2 mi"
            const durationText = element.duration.text; // e.g., "25 mins"
            
            // Parse distance (remove " mi" and convert to number)
            const miles = parseFloat(distanceText.replace(/[^0-9.]/g, ''));
            
            // Parse duration to hours
            const durationMinutes = element.duration.value / 60;
            const hours = Math.ceil(durationMinutes / 60 * 10) / 10; // Round up to nearest 0.1
            
            setFormData(prev => ({
              ...prev,
              estimated_miles: miles.toFixed(1),
              estimated_hours: Math.max(hours, 1).toFixed(1),
              estimated_duration_text: durationText,
              driver_return_miles: miles.toFixed(1) // Same as trip for return
            }));
            
            toast.success(`Route calculated: ${distanceText}, ${durationText}`);
          } else {
            toast.error('Could not calculate route. Please enter distances manually.');
          }
        }
      );
    } catch (error) {
      setCalculatingRoute(false);
      toast.error('Error calculating route');
    }
  }, [formData.pickup_location, formData.dropoff_location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const selectServiceOption = (optionId) => {
    setFormData(prev => ({ ...prev, service_option: optionId }));
    setStep(2);
  };

  const calculateQuote = () => {
    const miles = parseFloat(formData.estimated_miles) || 0;
    const hours = parseFloat(formData.estimated_hours) || 1;
    
    let pickup_fee = 0;
    let hourly_cost = 0;
    let mileage_cost = 0;
    let return_trip_cost = 0;
    
    if (formData.service_option === 'personal_chauffeur') {
      // $65/hour flat
      hourly_cost = Math.max(hours, 1) * 65;
    } else {
      // $150 flat fee + mileage (internal calculation)
      pickup_fee = 150;
      mileage_cost = miles * 0.60;
      
      if (formData.service_option === 'take_car_home') {
        const returnMiles = parseFloat(formData.driver_return_miles) || miles;
        return_trip_cost = 30 + (returnMiles * 0.60);
      }
    }
    
    let base_total = pickup_fee + hourly_cost + mileage_cost;
    
    // Luxury premium
    let luxury_surcharge = 0;
    if (formData.vehicle_type === 'luxury' || formData.vehicle_type === 'exotic') {
      luxury_surcharge = base_total * 0.50;
    }
    
    const total = base_total + return_trip_cost + luxury_surcharge;
    
    return {
      pickup_fee: pickup_fee.toFixed(2),
      hourly_cost: hourly_cost.toFixed(2),
      mileage_cost: mileage_cost.toFixed(2),
      return_trip_cost: return_trip_cost.toFixed(2),
      luxury_surcharge: luxury_surcharge.toFixed(2),
      total: total.toFixed(2),
      hours_charged: hours,
      miles_charged: miles
    };
  };

  const goToContactStep = () => {
    if (!formData.pickup_location || !formData.dropoff_location || !formData.date || !formData.time) {
      toast.error('Please fill in all trip details');
      return;
    }
    
    if (formData.service_option === 'personal_chauffeur' && !formData.estimated_hours) {
      toast.error('Please enter estimated hours for chauffeur service');
      return;
    }
    
    if (formData.service_option !== 'personal_chauffeur' && !formData.estimated_miles) {
      toast.error('Please calculate or enter the trip distance');
      return;
    }
    
    const calculatedQuote = calculateQuote();
    setQuote(calculatedQuote);
    setStep(3);
  };

  const submitBooking = async () => {
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in your contact information');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/public/drive-your-car/book`, {
        ...formData,
        estimated_miles: parseFloat(formData.estimated_miles) || 0,
        estimated_hours: parseFloat(formData.estimated_hours) || 1,
        driver_return_miles: parseFloat(formData.driver_return_miles) || 0,
        quoted_total: parseFloat(quote.total)
      });
      
      setPaymentLink(response.data.payment_link);
      setStep(4);
      toast.success('Booking submitted! Check your email for the payment link.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit booking');
    } finally {
      setLoading(false);
    }
  };

  const getServiceLabel = () => {
    const option = serviceOptions.find(o => o.id === formData.service_option);
    return option?.title || 'Drive Your Car';
  };

  // Step 4: Confirmation
  if (step === 4) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-black py-4 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <img src={LOGO_URL} alt="Onyx Reserve" className="h-12" />
            <Button variant="outline" className="text-white border-white hover:bg-white hover:text-black" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </header>

        <div className="max-w-2xl mx-auto py-16 px-4">
          <Card className="text-center">
            <CardContent className="pt-8">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Booking Submitted!</h2>
              <p className="text-gray-600 mb-6">
                Thank you, {formData.name}! Your {getServiceLabel()} booking has been received.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
                <h3 className="font-semibold text-gray-900 mb-4">Booking Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service:</span>
                    <span className="font-medium">{getServiceLabel()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">From:</span>
                    <span className="font-medium text-right max-w-[200px]">{formData.pickup_location}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">To:</span>
                    <span className="font-medium text-right max-w-[200px]">{formData.dropoff_location}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date:</span>
                    <span className="font-medium">{formData.date} at {formData.time}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-semibold">Total:</span>
                    <span className="font-bold text-green-600">${quote?.total}</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-blue-800 text-sm">
                  <strong>Next Step:</strong> A secure payment link has been sent to your email. Complete payment to confirm your booking.
                </p>
              </div>

              {paymentLink && (
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700 mb-4"
                  onClick={() => window.open(paymentLink, '_blank')}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Pay Now - ${quote?.total}
                </Button>
              )}

              <Button variant="outline" className="w-full" onClick={() => navigate('/')}>

                Back to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <img src={LOGO_URL} alt="Onyx Reserve" className="h-12" />
          <Button variant="outline" className="text-white border-white hover:bg-white hover:text-black" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto py-12 px-4">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Car className="h-4 w-4" />
            Designated Driver Service
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Drive Your Car</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Had a few drinks? Need a driver? Our professional drivers will get you AND your car home safely.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-10">
          <div className="flex items-center gap-2 sm:gap-4">
            {[
              { num: 1, label: 'Service' },
              { num: 2, label: 'Trip Details' },
              { num: 3, label: 'Contact' },
              { num: 4, label: 'Confirm' }
            ].map((s, i) => (
              <React.Fragment key={s.num}>
                <div className={`flex items-center gap-2 ${step >= s.num ? 'text-purple-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= s.num ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>
                    {s.num}
                  </div>
                  <span className="font-medium hidden sm:inline">{s.label}</span>
                </div>
                {i < 3 && <div className="w-6 sm:w-12 h-0.5 bg-gray-200"></div>}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step 1: Service Selection */}
        {step === 1 && (
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-center mb-6">What do you need?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {serviceOptions.map((option) => (
                <Card 
                  key={option.id}
                  className="cursor-pointer transition-all hover:shadow-lg hover:scale-105"
                  onClick={() => selectServiceOption(option.id)}
                >
                  <CardContent className="pt-6 text-center">
                    <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                      option.color === 'purple' ? 'bg-purple-100' : 
                      option.color === 'blue' ? 'bg-blue-100' : 'bg-green-100'
                    }`}>
                      <option.icon className={`h-8 w-8 ${
                        option.color === 'purple' ? 'text-purple-600' : 
                        option.color === 'blue' ? 'text-blue-600' : 'text-green-600'
                      }`} />
                    </div>
                    <h3 className="font-bold text-lg mb-2">{option.title}</h3>
                    <p className="text-gray-600 text-sm mb-3">{option.description}</p>
                    <p className="text-sm font-semibold text-purple-600">{option.pricing}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Trip Details */}
        {step === 2 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Trip Details</CardTitle>
                  <CardDescription>Enter your pickup and dropoff locations</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Locations with Google Maps */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-purple-600" />
                      Route
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="pickup_location">Pickup Location *</Label>
                        <Input
                          ref={pickupInputRef}
                          id="pickup_location"
                          name="pickup_location"
                          value={formData.pickup_location}
                          onChange={handleInputChange}
                          placeholder="Enter address or place name"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="dropoff_location">Dropoff Location *</Label>
                        <Input
                          ref={dropoffInputRef}
                          id="dropoff_location"
                          name="dropoff_location"
                          value={formData.dropoff_location}
                          onChange={handleInputChange}
                          placeholder="Enter address or place name"
                          className="mt-1"
                        />
                      </div>
                      
                      <Button 
                        type="button"
                        variant="outline"
                        onClick={calculateRoute}
                        disabled={!formData.pickup_location || !formData.dropoff_location || calculatingRoute}
                        className="w-full"
                      >
                        {calculatingRoute ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Calculating...
                          </>
                        ) : (
                          <>
                            <Navigation className="h-4 w-4 mr-2" />
                            Calculate Distance & Time
                          </>
                        )}
                      </Button>
                      
                      {formData.estimated_miles && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center gap-4">
                            <div>
                              <div className="text-sm text-gray-500">Distance</div>
                              <div className="font-bold text-lg">{formData.estimated_miles} miles</div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-500">Est. Time</div>
                              <div className="font-bold text-lg">{formData.estimated_duration_text || `${formData.estimated_hours} hrs`}</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Date & Time */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-purple-600" />
                      When do you need us?
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="date">Date *</Label>
                        <Input
                          id="date"
                          name="date"
                          type="date"
                          value={formData.date}
                          onChange={handleInputChange}
                          min={new Date().toISOString().split('T')[0]}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="time">Time *</Label>
                        <Input
                          id="time"
                          name="time"
                          type="time"
                          value={formData.time}
                          onChange={handleInputChange}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Hours for Personal Chauffeur */}
                  {formData.service_option === 'personal_chauffeur' && (
                    <div className="space-y-4">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Timer className="h-5 w-5 text-purple-600" />
                        How many hours?
                      </h3>
                      <div>
                        <Label htmlFor="estimated_hours">Hours Needed *</Label>
                        <Input
                          id="estimated_hours"
                          name="estimated_hours"
                          type="number"
                          value={formData.estimated_hours}
                          onChange={handleInputChange}
                          placeholder="e.g., 3"
                          min="1"
                          step="0.5"
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">1 hour minimum</p>
                      </div>
                    </div>
                  )}

                  {/* Vehicle Type */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Car className="h-5 w-5 text-purple-600" />
                      Your Vehicle Type
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        { value: 'standard', label: 'Standard', desc: 'No surcharge' },
                        { value: 'luxury', label: 'Luxury', desc: '+50%' },
                        { value: 'exotic', label: 'Exotic', desc: '+50%' }
                      ].map((type) => (
                        <label
                          key={type.value}
                          className={`flex flex-col p-4 border rounded-lg cursor-pointer transition-all ${
                            formData.vehicle_type === type.value
                              ? 'border-purple-600 bg-purple-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="vehicle_type"
                            value={type.value}
                            checked={formData.vehicle_type === type.value}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <span className="font-medium">{type.label}</span>
                          <span className="text-xs text-gray-500">{type.desc}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Button variant="outline" onClick={() => setStep(1)}>
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back
                    </Button>
                    <Button 
                      className="flex-1 bg-purple-600 hover:bg-purple-700"
                      onClick={goToContactStep}
                    >
                      Continue
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div>
              <Card className="sticky top-4">
                <CardHeader className="bg-purple-600 text-white rounded-t-lg">
                  <CardTitle className="text-lg">{getServiceLabel()}</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  {formData.service_option === 'personal_chauffeur' ? (
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Rate</span>
                        <span className="font-bold text-2xl text-purple-600">$65/hr</span>
                      </div>
                      <p className="text-gray-500 text-xs">
                        Flat hourly rate - we drive you around in your car
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Starting at</span>
                        <span className="font-bold text-2xl text-purple-600">$150</span>
                      </div>
                      <p className="text-gray-500 text-xs">
                        Price based on distance. Enter locations to calculate your exact fare.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Step 3: Contact Info */}
        {step === 3 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Your Information</CardTitle>
                  <CardDescription>Complete your booking</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Vehicle Details */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Car className="h-5 w-5 text-purple-600" />
                      Vehicle Details (helps our driver find your car)
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <Label htmlFor="vehicle_year">Year</Label>
                        <Input id="vehicle_year" name="vehicle_year" value={formData.vehicle_year} onChange={handleInputChange} placeholder="2024" />
                      </div>
                      <div>
                        <Label htmlFor="vehicle_make">Make</Label>
                        <Input id="vehicle_make" name="vehicle_make" value={formData.vehicle_make} onChange={handleInputChange} placeholder="BMW" />
                      </div>
                      <div>
                        <Label htmlFor="vehicle_model">Model</Label>
                        <Input id="vehicle_model" name="vehicle_model" value={formData.vehicle_model} onChange={handleInputChange} placeholder="M5" />
                      </div>
                      <div>
                        <Label htmlFor="vehicle_color">Color</Label>
                        <Input id="vehicle_color" name="vehicle_color" value={formData.vehicle_color} onChange={handleInputChange} placeholder="Black" />
                      </div>
                    </div>
                  </div>

                  {/* Contact */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <User className="h-5 w-5 text-purple-600" />
                      Contact Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input id="name" name="name" value={formData.name} onChange={handleInputChange} placeholder="John Smith" />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone *</Label>
                        <Input id="phone" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="(305) 555-1234" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="john@email.com" />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <Label htmlFor="notes">Special Instructions</Label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Where will you be? Any special instructions..."
                      className="w-full p-3 border rounded-lg resize-none h-20 mt-1"
                    />
                  </div>

                  <div className="flex gap-4">
                    <Button variant="outline" onClick={() => setStep(2)}>
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back
                    </Button>
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={submitBooking}
                      disabled={loading}
                    >
                      {loading ? (
                        <><Loader2 className="h-4 w-4 animate-spin mr-2" />Submitting...</>
                      ) : (
                        <><CheckCircle className="h-4 w-4 mr-2" />Confirm - ${quote?.total}</>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quote Summary */}
            <div>
              <Card className="sticky top-4">
                <CardHeader className="bg-green-600 text-white rounded-t-lg">
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Your Price
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="text-sm text-gray-600 pb-2 border-b">{getServiceLabel()}</div>
                    
                    {formData.service_option === 'personal_chauffeur' && (
                      <div className="text-sm text-gray-600">
                        {quote?.hours_charged} hours of service
                      </div>
                    )}
                    
                    {parseFloat(quote?.luxury_surcharge) > 0 && (
                      <div className="text-sm text-purple-600">
                        Includes luxury vehicle premium
                      </div>
                    )}
                    
                    <div className="pt-3">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-lg">Total</span>
                        <span className="font-bold text-3xl text-green-600">${quote?.total}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriveYourCar;
